// External Imports
import { h } from 'preact';
import { Button, Stack, Title, pxToRem, styled } from '@lessonnine/design-system.lib';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

import { Carousel, controlsPositionValue, slideEffectValue } from '../Carousel/Carousel';
import { getClasses } from './classes';
import { ClassCard } from './ClassCard';
import { useStore } from '../../state/context';
import { DesktopCTA } from './DesktopCTA';
import { siteMaxWidth } from '../LayoutWithSiteMaxWidth/LayoutWithSiteMaxWidth';
import { pxToRemParse } from '../../utils/styles';

/*
   We use two containers for Desktop VS Mobile Carousel, this allows us to show/hide the right Carousel based on the use-case
   It's needed to use two containers because our Carousel isn't able to responsively change its number of displayed items
*/

// Somehow without the extra div the carousel goes wild and takes a crazy amount of space, so containing it with the extra div
const DesktopCarouselContainer = styled.div`
  display: none;
  max-width: ${pxToRem(siteMaxWidth)};

  @media (min-width: ${tokens.viewports.breakpoints.small}) {
    display: block;
  }
`;

const MobileCarouselContainer = styled.div`
  display: block;
  max-width: 100%;

  @media (min-width: ${tokens.viewports.breakpoints.small}) {
    display: none;
  }
`;

const BrandColorText = styled.span`
  color: ${({ theme }) => theme.color.brand.default};
  font-style: italic;
`;

function ClassesSection() {
  const state = useStore();
  const { getDropdownLink, t } = state;

  return (
    <Stack
      alignItems="center"
      gap={pxToRemParse(tokens.space.x10)}
      padding={{ medium: [pxToRem(80), pxToRem(220)], small: [pxToRem(48), 0] }}
    >
      <Title as="h2" size={{ medium: 'large', small: 'small' }} textAlign="center">
        {t.classes.headline.part1} <BrandColorText>{t.classes.headline.part2}</BrandColorText>
      </Title>
      <DesktopCarouselContainer>
        <Carousel
          controlsPosition={controlsPositionValue.bottomCenter}
          slideEffect={slideEffectValue.zoom}
          slidesToShow={3}
        >
          {getClasses(t).map((liveClass) => (
            <ClassCard
              cta={
                <DesktopCTA href={getDropdownLink(liveClass.languageCode)}>
                  {t.classes.cta}
                </DesktopCTA>
              }
              key={liveClass.title}
              // eslint-disable-next-line react/jsx-props-no-spreading -- useful here as we pass along the whole class detail
              {...liveClass}
            />
          ))}
        </Carousel>
      </DesktopCarouselContainer>
      <MobileCarouselContainer>
        <Carousel controlsPosition={controlsPositionValue.bottomCenter} slidesToShow={1}>
          {getClasses(t).map((liveClass) => (
            <ClassCard
              cta={
                <Button
                  as="a"
                  color="secondary-inverse"
                  href={getDropdownLink(liveClass.languageCode)}
                >
                  {t.classes.cta}
                </Button>
              }
              key={liveClass.title}
              // eslint-disable-next-line react/jsx-props-no-spreading -- useful here as we pass along the whole class detail
              {...liveClass}
            />
          ))}
        </Carousel>
      </MobileCarouselContainer>
    </Stack>
  );
}

export { ClassesSection };
